import { ComponentProps } from "react";
import {
  Anchor,
  AnchorProps,
  Separator,
  styled,
  Text,
  useMedia,
  View,
  YStack,
} from "tamagui";

import { GrayLogo } from "../../icons/gray-logo";
import { InfoIcon } from "../../icons/info-icon";
import { withIconRef } from "../../utils/with-icon-ref";

const FooterContainer = styled(View, {
  name: "FooterContainer",
  alignItems: "center",
  alignSelf: "center",
  width: "100%",
  variants: {
    media: {
      lg: {
        gap: "$5",
        paddingTop: "$15",
      },
      md: {
        gap: "$3",
      },
    },
  } as const,
  defaultVariants: {
    media: "lg",
  },
});

const FooterContentContainer = styled(View, {
  name: "FooterContentContainer",
  variants: {
    media: {
      lg: {
        gap: "$3",
        maxWidth: 1248,
        width: "100%",
        justifyContent: "space-between",
        flexDirection: "row",
      },
      md: {
        width: "100%",
        paddingHorizontal: 0,
        flexDirection: "column",
        gap: "$0.5",
        alignItems: "center",
      },
    },
  } as const,
  defaultVariants: {
    media: "lg",
  },
});

const DisclaimerText = styled(Text, {
  name: "Disclaimer",
  color: "$gray-600",
  fontFamily: "$subtitle",
  fontWeight: "$4",
  fontSize: "$2",
  lineHeight: "$2",
  $sm: {
    textAlign: "center",
  },
});

const LinksTitle = styled(Text, {
  name: "LinksTitle",
  color: "$gray-650",
  marginBottom: "$1",
  variants: {
    font: {
      lg: {
        fontFamily: "$body",
        fontWeight: "$7",
        fontSize: "$3",
        lineHeight: "$3",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

const LinkText = styled(Text, {
  name: "Link",
  color: "$gray-secondary-400",
  cursor: "pointer",
  variants: {
    font: {
      lg: {
        fontFamily: "$subtitle",
        fontWeight: "$4",
        fontSize: "$2",
        lineHeight: "$2",
      },
      md: {
        fontFamily: "$subtitle",
        fontWeight: "$4",
        fontSize: "$2",
        lineHeight: "$2",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

const Link: React.FC<
  AnchorProps & {
    font?: "lg" | "md";
  }
> = ({ font = "lg", children, href, ...rest }) => {
  if (href === undefined) {
    return <LinkText font={font}>{children}</LinkText>;
  }

  return (
    <Anchor href={href} {...rest} textDecorationLine="none" unstyled>
      <LinkText font={font}>{children}</LinkText>
    </Anchor>
  );
};

const LinksContainer = styled(View, {
  name: "LinksContainer",
  variants: {
    media: {
      lg: {
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        gap: "$0.5",
      },
      md: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "$2",
        paddingHorizontal: "$2",
      },
    },
  } as const,
});

const FooterTopLine = styled(Separator, {
  name: "FooterTopLine",
  width: "100%",
  borderBottomWidth: "$0.2",
  borderColor: "$gray-primary-900",
  maxWidth: 1248,
  paddingHorizontal: "$3",
});

const FooterLogoContainer = styled(View, {
  name: "FooterLogoContainer",
  variants: {
    media: {
      lg: {
        gap: "$1",
        justifyContent: "center",
        alignItems: "center",
      },
    },
  } as const,
});

const MailTo: React.FC<
  Omit<AnchorProps, "href" | "children"> & { email: string; font?: "lg" | "md" }
> = ({ email, font = "md", ...rest }) => {
  return (
    <Anchor
      href={`mailto:${email}`}
      {...rest}
      textDecorationLine="none"
      unstyled
    >
      <LinkText font={font} color={"$gray-950"}>
        {email}
      </LinkText>
    </Anchor>
  );
};

const CopyrightText = styled(Text, {
  name: "CopyrightText",
  color: "$gray-600",
  variants: {
    font: {
      lg: {
        fontFamily: "$subtitle",
        fontWeight: "$5",
        fontSize: "$2",
        lineHeight: "$2",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

type FooterBaseProps = ComponentProps<typeof FooterContainer> & {
  topLineProps?: ComponentProps<typeof FooterTopLine>;
};

export const FooterBase: React.FC<FooterBaseProps> = ({
  children,
  topLineProps,
  ...rest
}) => {
  const { sm } = useMedia();
  return (
    <FooterContainer media={sm ? "md" : "lg"} {...rest}>
      <FooterContentContainer media={sm ? "md" : "lg"}>
        <FooterTopLine {...topLineProps} />
      </FooterContentContainer>
      {children}
    </FooterContainer>
  );
};

const DisclaimerIcon = styled(withIconRef(InfoIcon), {
  color: "$gray-600",
  width: 24,
  height: 24,
});

const Disclaimer: React.FC<{ children: string }> = ({ children }) => {
  return (
    <YStack gap="$0.5" $sm={{ alignItems: "center", maxWidth: "75%" }}>
      <DisclaimerIcon />
      <DisclaimerText>{children}</DisclaimerText>
    </YStack>
  );
};

export const Divider = styled(Separator, {
  name: "Divider",
  width: "75%",
  alignSelf: "center",
  borderColor: "$gray-secondary-400",
});

export const Footer = {
  Container: FooterBase,
  Content: FooterContentContainer,
  Links: LinksContainer,
  LinksTitle,
  Link,
  Disclaimer,
  DisclaimerText,
  Logo: GrayLogo,
  LogoContainer: FooterLogoContainer,
  MailTo,
  CopyrightText,
  Divider,
};
